import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  Calendar, 
  Sparkles, 
  BookOpen,
  CheckCircle2,
  Clock
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Plan } from "@/lib/api";

interface PlanDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: Plan | null;
}

export default function PlanDetailsModal({ open, onOpenChange, plan }: PlanDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  if (!plan) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return "status-success";
      case 'active':
        return "bg-primary/20 text-primary border-primary/30";
      case 'draft':
        return "status-warning";
      default:
        return "bg-secondary/20 text-secondary border-secondary/30";
    }
  };

  const topicColors: Record<string, string> = {
    "Executing": "bg-secondary/10 text-brand-green-dark border-secondary/25",
    "Strategic Thinking": "bg-primary/10 text-primary border-primary/25",
    "Relationship Building": "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25",
    "Influencing": "bg-warning/10 text-warning border-warning/25",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] p-0 bg-card overflow-hidden">
        <DialogHeader className="p-4 pb-4 border-b border-border/50 sm:p-6">
          <div className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <DialogTitle className={`min-w-0 break-words text-xl font-bold flex-1 sm:text-2xl ${isRTL ? 'text-right' : 'text-left'}`}>
                {plan.title}
              </DialogTitle>
              <Badge className={getStatusColor(plan.status)}>
                {t(`plans.${plan.status}`) || plan.status}
              </Badge>
            </div>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span>{plan.aiGeneratedContent?.duration || `${plan.duration} weeks`}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-secondary" />
                <span>{formatDate(plan.generatedAt)}</span>
              </div>
            </div>

            {/* Topics */}
            {plan.aiGeneratedContent?.topics && plan.aiGeneratedContent.topics.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {plan.aiGeneratedContent.topics.map((topic, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className={topicColors[topic] || "bg-secondary/20 text-secondary border-secondary/30"}
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    {topic}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-4 py-4 max-h-[calc(90vh-250px)] sm:px-6">
          <div className="space-y-6">
            {/* Top Strengths Section */}
            {plan.aiGeneratedContent?.strengths && plan.aiGeneratedContent.strengths.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  {t('plans.topStrengths') || 'Top Strengths'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {plan.aiGeneratedContent.strengths.map((strength, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-primary/10 text-primary border-primary/30 px-3 py-1"
                    >
                      {strength}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Plan Sections */}
            {plan.aiGeneratedContent?.sections && plan.aiGeneratedContent.sections.length > 0 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {t('plans.planDetails') || 'Plan Details'}
                </h3>

                <div className="space-y-6">
                  {plan.aiGeneratedContent.sections.map((section, idx) => (
                    <div key={idx} className="space-y-3">
                      <h4 className={`text-base font-semibold text-primary ${isRTL ? 'text-right' : 'text-left'}`}>
                        {section.heading}
                      </h4>
                      <div className={`space-y-2 text-sm text-foreground/90 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                        {section.content.map((item, itemIdx) => (
                          <p
                            key={itemIdx}
                            className={`${item.startsWith('•') || item.startsWith('    •') ? 'pl-4' : ''} whitespace-pre-wrap break-words`}
                          >
                            {item}
                          </p>
                        ))}
                      </div>
                      {idx < plan.aiGeneratedContent.sections.length - 1 && (
                        <Separator className="mt-4" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="p-4 pt-4 border-t border-border/50 sm:p-6">
          <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>
              {t('plans.createdAt') || 'Created'}: {formatDate(plan.createdAt)}
            </span>
            <span>
              {t('plans.updatedAt') || 'Updated'}: {formatDate(plan.updatedAt)}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
